import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, FileText, Loader2, CheckCircle2, BookOpen } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'token';

const authHeaders = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * Admin page: upload a .docx training document, preview the TOC, persist
 * chapters in DB, then attach each chapter (H1) to one or more formations.
 */
export const CourseContentAdmin = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [persisting, setPersisting] = useState(false);
  const [chapters, setChapters] = useState([]);
  const [formations, setFormations] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [chRes, coRes] = await Promise.all([
        axios.get(`${API}/api/course-content/chapters`, { headers: authHeaders() }),
        axios.get(`${API}/api/courses`),
      ]);
      setChapters(chRes.data || []);
      setFormations(coRes.data || []);
    } catch (e) {
      toast.error('Impossible de charger les chapitres.');
    }
    setLoading(false);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [chRes, coRes] = await Promise.all([
          axios.get(`${API}/api/course-content/chapters`, { headers: authHeaders() }),
          axios.get(`${API}/api/courses`),
        ]);
        if (!cancelled) {
          setChapters(chRes.data || []);
          setFormations(coRes.data || []);
        }
      } catch (e) {
        if (!cancelled) toast.error('Impossible de charger les chapitres.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onUpload = async () => {
    if (!file) return;
    setImporting(true);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/api/course-content/import/preview`, fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
      toast.success(`${res.data.total_chapters} chapitres détectés (${res.data.total_words.toLocaleString('fr-FR')} mots)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de parsing.');
    }
    setImporting(false);
  };

  const onConfirm = async (replaceAll) => {
    if (!preview?.token) return;
    setPersisting(true);
    try {
      const res = await axios.post(
        `${API}/api/course-content/import/confirm`,
        { token: preview.token, replace_all: !!replaceAll },
        { headers: authHeaders() },
      );
      toast.success(`${res.data.inserted} chapitres enregistrés.`);
      setPreview(null);
      setFile(null);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'enregistrement.");
    }
    setPersisting(false);
  };

  // Group chapters into H1 with descendants for display
  const chaptersGrouped = useMemo(() => {
    const h1s = chapters.filter((c) => c.level === 1);
    return h1s.map((h1) => ({
      ...h1,
      children: chapters.filter((c) => c.level > 1 && (
        c.parent_id === h1.id ||
        chapters.some((p) => p.id === c.parent_id && p.parent_id === h1.id)
      )),
    }));
  }, [chapters]);

  const toggleFormation = async (chapterId, formationId, has) => {
    try {
      await axios.post(
        `${API}/api/course-content/chapters/bulk-assign`,
        { chapter_ids: [chapterId], formation_id: formationId, mode: has ? 'remove' : 'add' },
        { headers: authHeaders() },
      );
      setChapters((prev) => prev.map((c) => c.id === chapterId
        ? {
            ...c,
            formation_ids: has
              ? (c.formation_ids || []).filter((id) => id !== formationId)
              : [...(c.formation_ids || []), formationId],
          }
        : c));
    } catch (e) {
      toast.error("Impossible de mettre à jour l'attribution.");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="course-content-admin">
      <header className="mb-10">
        <p className="text-xs font-mono tracking-[0.3em] text-[#C9B896] mb-3">COURS EN LIGNE — ADMINISTRATION</p>
        <h1 className="font-serif text-4xl md:text-5xl text-[#1F2A4A] font-light">Support de cours digital</h1>
        <p className="mt-3 text-[#4A5568] font-light max-w-3xl">
          Importe ton document Word complet (140 pages, styles Titre 1 / Titre 2 / Titre 3) et attribue chaque chapitre aux formations concernées.
          Les élèves inscrits aux formations correspondantes y auront accès gratuitement, en lecture seule.
        </p>
      </header>

      {/* IMPORT BLOCK */}
      <section className="mb-14 bg-white border border-[#C9B896]/30 rounded-2xl p-6 md:p-10">
        <div className="flex items-center gap-3 mb-5">
          <Upload size={20} className="text-[#C9B896]" />
          <h2 className="font-serif text-2xl text-[#1F2A4A]">1 — Importer un .docx</h2>
        </div>
        <div className="flex flex-col md:flex-row gap-4 items-start">
          <input
            type="file"
            accept=".docx"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
            data-testid="docx-input"
            className="block text-sm text-[#4A5568] file:mr-4 file:py-2.5 file:px-5 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#1F2A4A] file:text-white hover:file:bg-[#1F2A4A]/90 cursor-pointer"
          />
          <button
            onClick={onUpload}
            disabled={!file || importing}
            data-testid="docx-upload-btn"
            className="px-6 py-2.5 bg-[#C9B896] hover:bg-[#1F2A4A] text-[#1F2A4A] hover:text-white rounded-full text-sm font-semibold transition-all disabled:opacity-40 inline-flex items-center gap-2"
          >
            {importing ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
            {importing ? 'Analyse...' : 'Analyser le sommaire'}
          </button>
        </div>

        {preview && (
          <div className="mt-8 border-t border-[#C9B896]/30 pt-6" data-testid="docx-preview">
            <p className="text-sm text-[#4A5568] mb-4">
              <span className="font-semibold text-[#1F2A4A]">{preview.total_chapters}</span> chapitres détectés
              · <span className="font-semibold text-[#1F2A4A]">{preview.total_words.toLocaleString('fr-FR')}</span> mots
            </p>
            <div className="max-h-80 overflow-auto bg-[#FAF7F0] rounded-xl border border-[#C9B896]/20 p-4">
              <ul className="text-sm space-y-1">
                {preview.preview.map((p) => (
                  <li
                    key={p.slug}
                    className={`pl-${(p.level - 1) * 4} text-[#1F2A4A] ${p.level === 1 ? 'font-semibold mt-2' : p.level === 2 ? 'opacity-80' : 'opacity-60 italic'}`}
                    style={{ paddingLeft: `${(p.level - 1) * 16}px` }}
                  >
                    {`${'·'.repeat(p.level)} `}{p.title}
                    <span className="ml-2 text-xs text-[#C9B896]">{p.word_count} mots</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              <button
                onClick={() => onConfirm(false)}
                disabled={persisting}
                data-testid="docx-confirm-append-btn"
                className="px-6 py-2.5 bg-[#1F2A4A] hover:bg-[#1F2A4A]/90 text-white rounded-full text-sm font-semibold transition-all disabled:opacity-40 inline-flex items-center gap-2"
              >
                {persisting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                Ajouter ces chapitres à la bibliothèque
              </button>
              <button
                onClick={() => {
                  if (window.confirm('Remplacer TOUS les chapitres existants par cet import ? Cette action est irréversible.')) {
                    onConfirm(true);
                  }
                }}
                disabled={persisting}
                data-testid="docx-confirm-replace-btn"
                className="px-6 py-2.5 border border-red-500/30 text-red-600 hover:bg-red-50 rounded-full text-sm font-semibold transition-all disabled:opacity-40"
              >
                Remplacer toute la bibliothèque
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ASSIGNMENT BLOCK */}
      <section className="bg-white border border-[#C9B896]/30 rounded-2xl p-6 md:p-10">
        <div className="flex items-center gap-3 mb-5">
          <BookOpen size={20} className="text-[#C9B896]" />
          <h2 className="font-serif text-2xl text-[#1F2A4A]">2 — Attribuer aux formations</h2>
          <span className="ml-auto text-xs text-[#4A5568]">
            {chapters.length} chapitres · {chaptersGrouped.length} sections principales
          </span>
        </div>

        {loading ? (
          <div className="py-16 text-center text-[#4A5568]"><Loader2 className="inline animate-spin" /> Chargement...</div>
        ) : chaptersGrouped.length === 0 ? (
          <div className="py-16 text-center text-[#4A5568]">
            Aucun chapitre. Importe ton document Word ci-dessus pour démarrer.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-[#C9B896]/40">
                <tr>
                  <th className="text-left py-3 px-3 font-mono text-xs tracking-wider text-[#1F2A4A]">CHAPITRE</th>
                  {formations.map((f) => (
                    <th key={f.id} className="px-3 py-3 text-center font-mono text-[10px] tracking-wider text-[#1F2A4A] min-w-[110px]">
                      {f.title.replace(/^Formation\s+/i, '').slice(0, 18)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {chaptersGrouped.map((h1) => (
                  <tr key={h1.id} className="border-b border-[#C9B896]/15 hover:bg-[#FAF7F0]/50">
                    <td className="py-3 px-3 align-top">
                      <p className="font-semibold text-[#1F2A4A]">{h1.title}</p>
                      {h1.children?.length > 0 && (
                        <p className="text-xs text-[#4A5568] mt-1">{h1.children.length} sous-sections</p>
                      )}
                    </td>
                    {formations.map((f) => {
                      const has = (h1.formation_ids || []).includes(f.id);
                      return (
                        <td key={f.id} className="px-3 py-3 text-center align-top">
                          <input
                            type="checkbox"
                            checked={has}
                            onChange={() => toggleFormation(h1.id, f.id, has)}
                            data-testid={`assign-${h1.slug}-${f.id}`}
                            className="w-5 h-5 accent-[#1F2A4A] cursor-pointer"
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default CourseContentAdmin;
